import {prepareSenderSurpriseCart,senderSurpriseCartOptions,completeSenderSurpriseCart} from '@theme/pb-sender-surprise';
import {
  buildSectionCacheKey,
  clearSectionHtmlCache,
  getBuilderCacheEpoch,
  sectionHtmlMatchesEpoch,
  syncBuilderCacheEpoch,
} from '@theme/pb-builder-section-cache';
import {
  applyDraftStateAfterInit,
  builderAlreadyOnPage,
  clearDraftAfterCartSuccess,
  loadDraftForProduct,
  resetBuilderStateForStartFresh,
  resolveDraftResume,
  shouldResetBuilderSessionOnOpen as shouldResetBuilderSessionOnOpenWithDraft,
  wireDraftAutoSave,
} from '@theme/pb-draft-persistence';
import { persistCartSessionsAfterAdd } from '@theme/pb-cart-session-runtime';
import { resolveBuilderCloseMode } from '@theme/pb-builder-close-mode';
import {
  buildCartSectionsPayload,
  getCartSectionIds,
  refreshCartSections,
} from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { GUARD_BYPASS_HEADER, GUARD_BYPASS_VALUE } from '@theme/pb-cart-add-guard';
import {
  isCollectionCardEntryManifest,
  readEntryManifest,
} from '@theme/pb-entry-manifest';
import { READINESS_PHASES, BUILDER_RUNTIME_ASSET_GROUPS, getRequiredBuilderRuntimeAssets } from '@theme/pb-module-manifest';
import {
  ensureOfferBundleSyncInstalled,
  isOfferBundleSyncInstalled,
} from '@theme/pb-offer-bundle-sync-install';
import {
  areOfferRenderDepsReady,
  assertOfferRenderDepsReady,
  ensureBuilderInitDepsReady,
  ensureFoundationModulesReady,
  ensureOfferRenderDepsReady,
  registerReadinessPhaseRunner,
} from '@theme/pb-entry-readiness';

export { builderAlreadyOnPage, getCartSectionIds, refreshCartSections };

/** PB-FND-54 — readiness contract lives in pb-entry-readiness; re-export for existing importers. */
export {
  areBuilderInitDepsReady,
  areOfferRenderDepsReady,
  ensureBuilderInitDepsReady,
  ensureFoundationModulesReady,
  ensureModulesReady,
  ensureModulesReadyForEntryManifest,
  ensureOfferRenderDepsReady,
} from '@theme/pb-entry-readiness';

export const SECTION_TYPE = 'product-builder-section';
export const BUILDER_CLOSE_RESUME_MS = 450;

export { resolveBuilderCloseMode } from '@theme/pb-builder-close-mode';

let pendingCartDrawerResume = null;

function getShopperCountryIso() {
  const fromShopify = window.Shopify && window.Shopify.country;
  if (fromShopify) return String(fromShopify).toUpperCase();
  const fromData = window.BUILDER_DATA && window.BUILDER_DATA.shopperCountryIso;
  if (fromData) return String(fromData).toUpperCase();
  return '';
}

function getSectionCacheKey(handle) {
  if (!handle) return '';
  const currencyMatch = document.cookie.match(/(?:^|;\s*)cart_currency=([^;]+)/);
  const currency = currencyMatch ? decodeURIComponent(currencyMatch[1]) : '';
  const pathMatch = (window.location.pathname || '').match(/^\/([a-z]{2}(?:-[a-z]{2})?)\//i);
  const locale = pathMatch ? pathMatch[1].toLowerCase() : '';
  const country = getShopperCountryIso();
  return buildSectionCacheKey(getBuilderCacheEpoch(window), handle, locale, currency, country);
}

const sectionHtmlCache = new Map();
const sectionCacheOrder = [];
const prefetchInFlight = new Map();
const sectionCacheState = { sectionHtmlCache, sectionCacheOrder, prefetchInFlight };

if (typeof window !== 'undefined') {
  syncBuilderCacheEpoch(window, undefined, sectionCacheState);
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      syncBuilderCacheEpoch(window, undefined, sectionCacheState);
    }
  });
}
/** @type {import('./cart-drawer.js').CartDrawerComponent | null} */
let suspendedCartDrawer = null;
/** @type {object | null} */
let closeResumeHookTarget = null;
let pbAssetsSessionLoaded = false;
let loadingShellAborted = false;
let loadingShellCloseWired = false;

export function getShopRoot() {
  return window.Shopify?.routes?.root || '/';
}

export function getThemeRoutes() {
  if (typeof Theme !== 'undefined' && Theme?.routes) return Theme.routes;
  const root = getShopRoot();
  return {
    cart_add_url: `${root}cart/add.js`,
    cart_change_url: `${root}cart/change.js`,
    cart_update_url: `${root}cart/update.js`,
    cart_url: `${root}cart`,
  };
}

export function readAssetUrls() {
  const el = document.getElementById('pb-cart-edit-assets');
  if (!el) return [];
  try {
    const parsed = JSON.parse(el.textContent || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

export function readTagModuleUrls() {
  const el = document.getElementById('pb-tag-module-assets');
  if (!el) return [];
  try {
    const parsed = JSON.parse(el.textContent || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

function tagModuleReadyForUrl(src) {
  if (/pb-button-layout\.js/.test(src)) return !!window.PBButtonLayout?.pickColumnCount;
  if (/pb-option-label\.js/.test(src)) return !!window.PBOptionLabel?.enhance;
  if (/pb-variant-pricing\.js/.test(src)) return !!window.PBVariantPricing?.applyVariantPriceBadges;
  if (/pb-navigation\.js/.test(src)) return !!window.PBNavigation?.createNavigationKernel;
  if (/pb-builder-lifecycle\.js/.test(src)) return !!window.PBBuilderLifecycle?.createBuilderLifecycle;
  if (/pb-offers\.js/.test(src)) return !!window.PBOffers?.createOffersPlugin;
  if (/pb-addons\.js/.test(src)) return !!window.PBAddons?.createAddonsPlugin;
  return true;
}

/** Load option-tag / pricing / layout scripts when lazy-mount skipped them. */
export async function ensureTagModulesReady() {
  const urls = readTagModuleUrls();
  for (const src of urls) {
    if (tagModuleReadyForUrl(src)) continue;
    await loadExternalScript(src, { force: true });
  }
}

export function warmTagModulesWhenIdle() {
  const run = () => {
    readTagModuleUrls().forEach((src) => {
      if (!tagModuleReadyForUrl(src)) {
        loadExternalScript(src).catch(() => {});
      }
    });
  };
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(run, { timeout: 3000 });
  } else {
    window.setTimeout(run, 1500);
  }
}

function resetPbAiImageModule() {
  window._pbAiImageModuleLoaded = false;
  delete window.PBAiImage;
}

function isPbAssetModuleScript(src) {
  return /pb-(media-upload|properties|field-runtime|cart-display-image|flush-encode|bundle-row-encode|finalize|entry-offer-autofill|entry-readiness|entry-manifest|module-manifest|builder-bootstrap|builder-tokens|modal-chrome|navigation|builder-lifecycle|offers|addons|validation-coordinator|topic-multi|draft-persistence|draft-continue|session-store|session-wire|open-step-resolver|step-dom|step-kinds|offers-render|addons-render|dl-ai-hydrate|design-library-snapshot|media-hydrate|field-ai-image|field-audio|field-upload|field-text|field-long-text|field-choice|field-topic-multi|field-design-library)\.js/.test(
    src
  );
}

function assetFilenameFromUrl(src) {
  if (!src) return '';
  const path = String(src).split('?')[0];
  const parts = path.split('/');
  return parts[parts.length - 1] || '';
}

function removeScriptsByAssetName(filename) {
  if (!filename || typeof document === 'undefined') return;
  document.querySelectorAll('script[src]').forEach((el) => {
    if (assetFilenameFromUrl(el.src) === filename) el.remove();
  });
}

const scriptLoadsInFlight = new Map();

function loadExternalScript(src, options) {
  const force = options?.force;
  if (!src) return Promise.resolve();
  if (scriptLoadsInFlight.has(src)) return scriptLoadsInFlight.get(src);
  const filename = assetFilenameFromUrl(src);
  if (force && filename) {
    removeScriptsByAssetName(filename);
    if (/pb-ai-image\.js/.test(filename)) resetPbAiImageModule();
  } else {
    const existing = document.querySelector(`script[src="${CSS.escape(src)}"]`);
    if (existing) return Promise.resolve();
  }
  const pending = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    if (isPbAssetModuleScript(src)) {
      script.type = 'module';
    } else {
      script.defer = true;
    }
    const loadTimeout = setTimeout(() => {
      script.onload = null;
      script.onerror = null;
      script.remove();
      reject(new Error('Failed to load ' + src));
    }, 20000);
    script.onload = () => {
      clearTimeout(loadTimeout);
      resolve();
    };
    script.onerror = () => {
      clearTimeout(loadTimeout);
      script.remove();
      reject(new Error('Failed to load ' + src));
    };
    document.head.appendChild(script);
  }).finally(() => scriptLoadsInFlight.delete(src));
  scriptLoadsInFlight.set(src, pending);
  return pending;
}

function removeStaleAssetScripts() {
  readAssetUrls().forEach((src) => {
    const el = document.querySelector(`script[src="${CSS.escape(src)}"]`);
    if (el) el.remove();
  });
  resetPbAiImageModule();
  pbAssetsSessionLoaded = false;
}

const SECTION_CACHE_MAX = 10;

function touchSectionCache(cacheKey) {
  const idx = sectionCacheOrder.indexOf(cacheKey);
  if (idx >= 0) sectionCacheOrder.splice(idx, 1);
  sectionCacheOrder.push(cacheKey);
  while (sectionCacheOrder.length > SECTION_CACHE_MAX) {
    const oldest = sectionCacheOrder.shift();
    if (oldest) sectionHtmlCache.delete(oldest);
  }
}

function builderSectionUrl(handle) {
  let url = `${getShopRoot()}products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(SECTION_TYPE)}`;
  try {
    const previewId =
      new URLSearchParams(window.location.search).get('preview_theme_id') ||
      window.__PB_PREVIEW_THEME_ID__;
    if (previewId) {
      url += `&preview_theme_id=${encodeURIComponent(previewId)}`;
    }
  } catch (_err) {
    /* ignore */
  }
  return url;
}

export function isBuilderHtmlCached(handle) {
  return !!handle && sectionHtmlCache.has(getSectionCacheKey(handle));
}

export function prefetchBuilderSectionHtml(handle) {
  const cacheKey = getSectionCacheKey(handle);
  if (!handle || sectionHtmlCache.has(cacheKey)) return Promise.resolve(true);
  if (prefetchInFlight.has(cacheKey)) return prefetchInFlight.get(cacheKey);

  const promise = fetch(builderSectionUrl(handle), { credentials: 'same-origin', cache: 'no-store' })
    .then((res) => {
      if (!res.ok) throw new Error('Could not prefetch product builder');
      return res.text();
    })
    .then((html) => {
      if (sectionHtmlMatchesEpoch(html, getBuilderCacheEpoch(window))) {
        sectionHtmlCache.set(cacheKey, html);
        touchSectionCache(cacheKey);
      }
      return true;
    })
    .catch(() => false)
    .finally(() => {
      prefetchInFlight.delete(cacheKey);
    });

  prefetchInFlight.set(cacheKey, promise);
  return promise;
}

export function ensureFieldRuntimeOnBuilder(pb) {
  const target = pb || window.productBuilder;
  if (typeof globalThis.__pbEnsureFieldRuntimeInstalled === 'function') {
    return globalThis.__pbEnsureFieldRuntimeInstalled(target);
  }
  return typeof target?.getFieldRegistry === 'function';
}

/** PB asset scripts attach to window.productBuilder; they must load after inline builder logic. */
export async function ensurePbModulesReady() {
  if (!window.productBuilder) return;
  const urls = readAssetUrls();
  const need = new Set();
  if (!window.productBuilder.encodeBuilderStateForCart) {
    urls.filter((u) => /pb-properties\.js/.test(u)).forEach((u) => need.add(u));
  }
  if (
    typeof window.productBuilder.getFieldRegistry !== 'function' ||
    typeof window.productBuilder.buildSubFieldCtx !== 'function'
  ) {
    urls.filter((u) => /pb-field-runtime\.js/.test(u)).forEach((u) => need.add(u));
  }
  if (typeof window.productBuilder.applyButtonWrap !== 'function') {
    urls.filter((u) => /pb-button-layout\.js/.test(u)).forEach((u) => need.add(u));
  }
  for (const src of need) {
    try {
      await loadExternalScript(src, { force: true });
    } catch (err) {
      console.warn('[pb-builder-launch] PB module load skipped:', src, err?.message || err);
    }
  }
  await ensureRequiredBuilderRuntimes();
  ensureFieldRuntimeOnBuilder();
  pbAssetsSessionLoaded = true;
}

function installFoundationModuleHooks() {
  const pb = window.productBuilder;
  if (!pb) return;
  ensureFieldRuntimeOnBuilder(pb);
  if (typeof globalThis.__pbEnsureTopicMulti === 'function') {
    globalThis.__pbEnsureTopicMulti(pb);
  }
  if (typeof globalThis.__pbEnsureValidationCoordinator === 'function') {
    globalThis.__pbEnsureValidationCoordinator(pb);
  }
  if (typeof globalThis.__pbEnsureBuilderBootstrap === 'function') {
    globalThis.__pbEnsureBuilderBootstrap(pb);
  }
  if (typeof globalThis.__pbEnsureBuilderTokens === 'function') {
    globalThis.__pbEnsureBuilderTokens(pb);
  }
  if (typeof globalThis.__pbEnsureModalChrome === 'function') {
    globalThis.__pbEnsureModalChrome(pb);
  }
  if (typeof globalThis.__pbEnsureFinalize === 'function') {
    globalThis.__pbEnsureFinalize(pb);
  }
  if (typeof pb._wireLifecycleStepPlugins === 'function') {
    pb._wireLifecycleStepPlugins();
  }
}

/** Local encode-belt probe — avoid static import of pb-flush-encode (stale CDN cache breaks cart edit load). */
function isEncodeBeltReady(pb) {
  const target = pb || (typeof window !== 'undefined' ? window.productBuilder : null);
  if (window.PBFlushEncode?.areEncodeBeltReady) {
    return window.PBFlushEncode.areEncodeBeltReady(target);
  }
  return !!target && typeof target.encodeBuilderStateForCart === 'function';
}

function assertEncodeBeltReadyLocal(pb, context) {
  if (isEncodeBeltReady(pb)) return pb || window.productBuilder;
  const message =
    window.PBFlushEncode?.ENCODE_BELT_NOT_READY_MESSAGE ||
    'Personalization could not be saved. Please refresh the page and try again.';
  const err = new Error(message);
  err.code = 'PB_ENCODE_BELT_NOT_READY';
  if (context) err.context = context;
  throw err;
}

/** Bundle sync / offer autofill encode belt — required on collection portal mount (PDP loads via section). */
export async function ensureEncodeBeltModulesReady() {
  const imports = [];
  if (!window.PBFlushEncode?.encodeTier1Properties) {
    imports.push(import('@theme/pb-flush-encode'));
  }
  if (!window.PBBundleRowEncode?.encodeBundleRowProperties) {
    imports.push(import('@theme/pb-bundle-row-encode'));
  }
  if (!window.PBEntryOfferAutofill?.prepareOfferAutofillBeforeRender) {
    imports.push(import('@theme/pb-entry-offer-autofill'));
  }
  if (typeof window.PBCartDisplayImage?.resolveCartLineDisplayImageUrl !== 'function') {
    imports.push(import('@theme/pb-cart-display-image'));
  }
  if (imports.length) {
    await Promise.all(
      imports.map((p) =>
        p.catch((err) => {
          console.warn('[pb-builder-launch] Encode belt module load failed', err?.message || err);
        })
      )
    );
  }
  const pb = window.productBuilder;
  const needsProperties =
    !pb ||
    typeof pb.encodeBuilderStateForCart !== 'function' ||
    typeof pb.buildBundleEffectiveMainProps !== 'function' ||
    typeof pb.purgeInvalidAiMediaTier1Keys !== 'function';
  const needsFinalize =
    !pb ||
    typeof pb.encodeFlowStateForCart !== 'function' ||
    typeof pb.encodeCartLineProperties !== 'function';

  if (needsProperties) {
    try {
      const propsMod = await import('@theme/pb-properties');
      if (pb && typeof propsMod.installPbPropertiesOnBuilder === 'function') {
        propsMod.installPbPropertiesOnBuilder(pb);
      }
    } catch (err) {
      console.warn('[pb-builder-launch] pb-properties load failed', err?.message || err);
    }
    if (pb && typeof pb.encodeBuilderStateForCart !== 'function') {
      await ensurePbModulesReady();
    }
    if (pb && typeof pb.encodeBuilderStateForCart !== 'function') {
      document.dispatchEvent(new CustomEvent('builder:ready'));
    }
  }
  if (needsFinalize) {
    await import('@theme/pb-finalize').catch((err) => {
      console.warn('[pb-builder-launch] pb-finalize load failed', err?.message || err);
    });
    if (typeof window.__pbEnsureFinalize === 'function') {
      window.__pbEnsureFinalize(window.productBuilder);
    }
  }
  installBuilderCapabilities(window.productBuilder);

  const pbRef = window.productBuilder;
  if (pbRef && typeof pbRef.encodeBuilderStateForCart !== 'function') {
    assertEncodeBeltReadyLocal(pbRef, 'ensureEncodeBeltModulesReady');
  }
}

async function ensureFoundationEsModules() {
  const imports = [];
  if (typeof window.productBuilder?.buildTopicSteps !== 'function') {
    imports.push(import('@theme/pb-topic-multi'));
  }
  if (!window.PBValidation?.createValidationCoordinator) {
    imports.push(import('@theme/pb-validation-coordinator'));
  }
  if (typeof window.productBuilder?._ensureNavKernel !== 'function') {
    imports.push(import('@theme/pb-builder-bootstrap'));
  }
  if (typeof window.productBuilder?.reconcileStepDom !== 'function') {
    imports.push(import('@theme/pb-step-dom'));
  }
  if (typeof window.productBuilder?.isBuilderModalMeasurable !== 'function') {
    imports.push(import('@theme/pb-modal-chrome'));
  }
  if (!window.PBProductGuideStrip) {
    imports.push(import('@theme/pb-product-guide-strip'));
  }
  if (typeof window.productBuilder?.syncPersonalizationTemplates !== 'function') {
    imports.push(import('@theme/pb-builder-tokens'));
  }
  if (imports.length) {
    await Promise.all(
      imports.map((p) =>
        p.catch((err) => {
          console.warn('[pb-builder-launch] Foundation ES module load failed', err?.message || err);
        })
      )
    );
  }
  await ensureEncodeBeltModulesReady();
  if (typeof window.__pbEnsureModalChrome === 'function') {
    window.__pbEnsureModalChrome(window.productBuilder);
  }
  if (typeof window.__pbEnsureAiReviewHop === 'function') {
    window.__pbEnsureAiReviewHop(window.productBuilder);
  }
}

async function runFoundationModuleChain() {
  if (!window.productBuilder) return;
  await ensurePbModulesReady();
  await ensureBuilderSatelliteScriptsReady();
  await ensureOfferSupportModulesReady();
  await ensureStepPluginModulesReady();
  await ensureFoundationEsModules();
  installFoundationModuleHooks();
}

/**
 * Full offer paint stack — collection portal mount + offer step use same chain as PDP.
 * PB-FND-54 / collection_card offer-step parity: tag modules + bundle sync + finalize before paint.
 */
export async function runOfferRenderModuleChain() {
  const pb = window.productBuilder;
  if (!pb) return;
  await runFoundationModuleChain();
  await ensureTagModulesReady();
  await ensureBuilderCapabilitiesInstalled(pb);
  patchBuilderAugments(pb);
  const base = resolveThemeAssetBase();
  if (base && typeof window.PBVariantPricing?.calcBundleDisplayTotals !== 'function') {
    try {
      await loadExternalScript(`${base}pb-variant-pricing.js`, { force: true });
    } catch (err) {
      console.warn('[pb-builder-launch] variant-pricing load failed', err?.message || err);
    }
  }
  if (typeof window.__pbEnsureBuilderTokens === 'function') {
    window.__pbEnsureBuilderTokens(pb);
  }
  if (typeof window.__pbEnsureFinalize === 'function') {
    window.__pbEnsureFinalize(pb);
  }
  installBuilderCapabilities(pb);
  if (!isOfferBundleSyncPatched(pb)) {
    await ensureOfferBundleSyncPatched(pb);
    installBuilderCapabilities(pb);
  }
  assertOfferRenderDepsReady(pb);
}

registerReadinessPhaseRunner(READINESS_PHASES.TAG_MODULES, ensureTagModulesReady);
registerReadinessPhaseRunner(READINESS_PHASES.FOUNDATION, runFoundationModuleChain);
registerReadinessPhaseRunner(READINESS_PHASES.OFFER_RENDER, runOfferRenderModuleChain);

export function warmPbAssetsWhenIdle() {
  const run = async () => {
    // Collection cards have not selected a product yet. Do not warm every media engine.
    if (!window.productBuilder || !window.BUILDER_DATA?.mainProduct) return;
    try {
      await ensureRequiredBuilderRuntimes();
    } catch {
      // An actual open retries the same readiness boundary and reports the failure.
    }
  };
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(run, { timeout: 3000 });
  } else {
    window.setTimeout(run, 1500);
  }
}

const toolkitBuilders = new WeakSet();

async function ensureRequiredBuilderRuntimes() {
  const pb = window.productBuilder;
  if (!pb) return;
  const ready = {
    'pb-media-upload.js': () => typeof window.pbMediaUpload?.upload === 'function',
    'pb-audio-waveform.js': () => !!window.pbAudioWaveform,
    'pb-audio-engine.js': () => !!window.pbAudioEngine,
    'pb-longtext-recorder.js': () => !!window.pbLongTextRecorder,
    'pb-audio-recorder.js': () => typeof pb.startAudioRecording === 'function',
    'pb-upload.js': () => typeof pb.triggerUploadInput === 'function' && typeof pb.getUploadUrls === 'function',
    'pb-ai-image.js': () => typeof window.PBAiImage?.rehydrateWrap === 'function',
    'pb-toolkit.js': () => toolkitBuilders.has(pb) && !!window.PBToolkit,
    'pb-design-library-logic.js': () => !!window.PBDesignLibraryLogic?.parseDesignLibraryCartSnapshot,
    'pb-design-library.js': () => !!window.PBDesignLibrary?.flushCartPropsToSelections,
  };
  const required = getRequiredBuilderRuntimeAssets(window.BUILDER_DATA, window.builderState);
  const load = async (file) => {
    if (ready[file]()) return;
    const url = resolveThemeAssetUrl(file);
    if (!url) throw new Error('Could not load personalization tools. Please try again.');
    await loadExternalScript(url, { force: true });
    if (file === 'pb-toolkit.js') toolkitBuilders.add(pb);
    if (!ready[file]()) throw new Error('Personalization tools are still loading. Please try again.');
  };
  for (const group of BUILDER_RUNTIME_ASSET_GROUPS) {
    // Download independent libraries together; consumers wait for their dependencies.
    await Promise.all(group.filter((file) => required.has(file)).map(load));
  }
}

const PORTAL_AUGMENT_SCRIPT_RE =
  /pb-media-line-ownership\.js|pb-offer-bundle-sync\.js|pb-bundle-row-validation\.js|pb-offer-bundle-preview\.js|pb-offer-intro\.js|pb-offer-bundle-market-filter\.js|pb-offer-line-resolver\.js|pb-offer-line-patch\.js/;

export function preloadBuilderSectionScripts(container) {
  const hints = [];
  const seen = new Set();
  for (const script of container.querySelectorAll('script[src]')) {
    const src = script.src;
    if (!/\/pb-[^/]+\.js(?:\?|$)/.test(src) || seen.has(src)) continue;
    seen.add(src);
    const hint = document.createElement('link');
    hint.rel = script.type === 'module' || isPbAssetModuleScript(src) ? 'modulepreload' : 'preload';
    if (hint.rel === 'preload') hint.as = 'script';
    hint.href = src;
    if (script.crossOrigin) hint.crossOrigin = script.crossOrigin;
    if (script.integrity) hint.integrity = script.integrity;
    document.head.appendChild(hint);
    hints.push(hint);
  }
  return () => hints.forEach(hint => hint.remove());
}

export function runInlineScripts(container) {
  const clearHints = preloadBuilderSectionScripts(container);
  const scripts = container.querySelectorAll('script');
  return Array.from(scripts).reduce((chain, oldScript) => {
    return chain.then(() => {
      return new Promise((resolve) => {
        const finish = () => resolve();
        if (oldScript.src) {
          const src = oldScript.getAttribute('src') || oldScript.src;
          let injectedScript;
          const timeout =
            setTimeout(() => {
              console.warn('[pb-builder-launch] Script load timeout (continuing mount):', src);
              injectedScript?.remove();
              oldScript.remove();
              finish();
            }, 20000);
          const isTagModule =
            /pb-(button-layout|option-label|variant-pricing|navigation|builder-lifecycle|offers|addons)\.js/.test(
              src
            );
          const isAugment = PORTAL_AUGMENT_SCRIPT_RE.test(src);
          const existing = Array.from(document.querySelectorAll(`script[src="${CSS.escape(src)}"]`))
            .find(script => !container.contains(script));
          if (isAugment) {
            removeScriptsByAssetName(assetFilenameFromUrl(src));
          } else if (existing) {
            if (!isTagModule || tagModuleReadyForUrl(src)) {
              clearTimeout(timeout);
              oldScript.remove();
              finish();
              return;
            }
            existing.remove();
          }
          const script = document.createElement('script');
          injectedScript = script;
          script.src = src;
          if (
            oldScript.type === 'module' ||
            isPbAssetModuleScript(src) ||
            /pb-(navigation|builder-lifecycle|offers|addons)\.js/.test(src)
          ) {
            script.type = 'module';
          } else if (oldScript.defer) {
            script.defer = true;
          }
          script.onload = () => {
            clearTimeout(timeout);
            oldScript.remove();
            finish();
          };
          script.onerror = () => {
            clearTimeout(timeout);
            console.warn('[pb-builder-launch] Script failed (continuing mount):', src);
            script.remove();
            oldScript.remove();
            finish();
          };
          document.head.appendChild(script);
          return;
        }

        const script = document.createElement('script');
        script.textContent = oldScript.textContent;
        const parent = oldScript.parentNode;
        if (parent) {
          parent.insertBefore(script, oldScript);
          oldScript.remove();
        }
        finish();
      });
    });
  }, Promise.resolve()).finally(clearHints);
}

export function waitForBuilderReady(timeoutMs) {
  const deadline = Date.now() + (timeoutMs || 8000);
  return new Promise((resolve, reject) => {
    let timer;
    let settled = false;
    const ready = () => window.productBuilder?.ready && window.BUILDER_DATA?.mainProduct;
    function finish(error) {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      document.removeEventListener('builder:ready', onReady);
      if (error) reject(error);
      else resolve();
    }
    function onReady() {
      if (ready()) finish();
    }
    function check() {
      if (ready()) {
        finish();
        return;
      }
      if (Date.now() > deadline) {
        finish(new Error('Builder failed to load'));
        return;
      }
      timer = setTimeout(check, 50);
    }
    if (window.productBuilder?.ready && window.BUILDER_DATA?.mainProduct) {
      resolve();
      return;
    }
    document.addEventListener('builder:ready', onReady);
    check();
  });
}

/** Same product already mounted: resume progress (PDP + collection card reopen). */
export function shouldResetBuilderSessionOnOpen(productId, freshSessionRequested, context) {
  if (context && (context.builderAlreadyOnPage != null || context.draftExists != null)) {
    return shouldResetBuilderSessionOnOpenWithDraft(productId, freshSessionRequested, context);
  }
  return shouldResetBuilderSessionOnOpenWithDraft(productId, freshSessionRequested, {
    builderAlreadyOnPage: builderAlreadyOnPage(productId),
    draftExists: false,
  });
}

function resolveThemeAssetBase() {
  const sample = document.querySelector(
    'script[src*="pb-button-layout.js"], script[src*="pb-builder-launch.js"]'
  );
  const src = sample?.src || sample?.getAttribute?.('src') || '';
  const match = src.match(/^(.*\/assets\/)/);
  if (match) return match[1];

  const candidates = [...readTagModuleUrls(), ...readAssetUrls()];
  for (const url of candidates) {
    const baseMatch = String(url).match(/^(.*\/assets\/)/);
    if (baseMatch) return baseMatch[1];
  }
  return '';
}

/** Full CDN URL for a theme asset file — prefers pb-cart-edit-portal JSON lists. */
export function resolveThemeAssetUrl(filename) {
  const name = String(filename || '').trim();
  if (!name) return '';
  const fromList = [...readTagModuleUrls(), ...readAssetUrls()].find((url) =>
    assetFilenameFromUrl(url) === name
  );
  if (fromList) return fromList;
  const base = resolveThemeAssetBase();
  return base ? `${base}${name}` : '';
}

async function loadCapabilityScript(filename) {
  const url = resolveThemeAssetUrl(filename);
  if (!url) {
    throw new Error(`[pb-builder-launch] Unknown asset URL for ${filename}`);
  }
  await loadExternalScript(url, { force: true });
}

/** Classic satellite scripts (upload, button layout) — must exist before init/updateUI. */
export async function ensureBuilderSatelliteScriptsReady() {
  if (!window.productBuilder) return;
  const base = resolveThemeAssetBase();
  if (!base) return;

  const satellites = [
    {
      file: 'pb-button-layout.js',
      ready: () => typeof window.productBuilder.applyButtonWrap === 'function',
    },
    {
      file: 'pb-option-label.js',
      ready: () => !!window.PBOptionLabel?.enhance,
    },
  ];

  for (const { file, ready } of satellites) {
    if (ready()) continue;
    try {
      await loadExternalScript(resolveThemeAssetUrl(file), { force: true });
    } catch (err) {
      console.warn('[pb-builder-launch] Builder satellite load failed:', file, err?.message || err);
    }
  }
}

/** Full bundle-sync patch — offer paint requires maybeAutoApplyBundleSyncAll, not legacy sync-only. */
export function isOfferBundleSyncPatched(pb = window.productBuilder) {
  return isOfferBundleSyncInstalled(pb);
}

/**
 * Synchronous capability install — call immediately after every productBuilder is created
 * (portal mount, PDP, cart edit). Does not load scripts; patches onto the live pb instance.
 */
export function installBuilderCapabilities(pb = window.productBuilder) {
  if (!pb) return false;
  window.PBOfferLineResolver?.patchProductBuilderOfferLineGuards?.(pb);
  window.PBButtonLayout?.patchProductBuilder?.(pb);
  if (typeof window.PBBundleRowValidationPatch === 'function') {
    window.PBBundleRowValidationPatch(pb);
  }
  if (typeof window.PBOfferBundleSyncPatch === 'function') {
    window.PBOfferBundleSyncPatch(pb);
  }
  installFoundationModuleHooks();
  wrapFinishSubFlowBundleSync(pb);
  wrapFinishSubFlowEncodeBelt(pb);
  wrapFinalizeEncodeBelt(pb);
  return isOfferBundleSyncPatched(pb);
}

/** Portal mount + offer paint — load classic augment scripts if needed, then install. */
export async function ensureBuilderCapabilitiesInstalled(pb = window.productBuilder) {
  if (!pb) return;
  await ensureOfferSupportModulesReady();
  await ensureFoundationEsModules();
  installBuilderCapabilities(pb);
  if (!isOfferBundleSyncPatched(pb)) {
    await ensureOfferBundleSyncPatched(pb);
    installBuilderCapabilities(pb);
  }
}

/** Re-apply or force-reload pb-offer-bundle-sync when portal mount reused stale script tags. */
export async function ensureOfferBundleSyncPatched(pb = window.productBuilder) {
  if (!pb) return;
  const ok = await ensureOfferBundleSyncInstalled(pb, loadCapabilityScript);
  if (!ok) {
    console.warn(
      '[pb-builder-launch] bundle-sync install incomplete after forced load — offer paint may fail'
    );
  }
}

/** Offer/add-on list deps skipped when portal mount reuses stale script tags. */
export async function ensureOfferSupportModulesReady() {
  if (!window.productBuilder) return;

  const scripts = [
    {
      file: 'pb-offer-bundle-market-filter.js',
      ready: () => !!window.PBOfferBundleMarketFilter?.applyToBuilderDataSync,
    },
    {
      file: 'pb-bundle-row-validation.js',
      ready: () => typeof window.productBuilder?.resolveBundleSyncSourceIndex === 'function',
    },
    {
      file: 'pb-media-line-ownership.js',
      ready: () => !!window.PBMediaLineOwnership?.canSourceFanOutAiMedia,
    },
    {
      file: 'pb-offer-bundle-sync.js',
      ready: () => isOfferBundleSyncPatched(window.productBuilder),
    },
    {
      file: 'pb-offer-line-resolver.js',
      ready: () => !!window.PBOfferLineResolver?.patchProductBuilderOfferLineGuards,
    },
    {
      file: 'pb-offer-line-patch.js',
      ready: () => !!window.PBOfferLineResolver?.patchProductBuilderOfferLineGuards,
    },
    {
      file: 'pb-offer-bundle-preview.js',
      ready: () => typeof window.productBuilder?.buildBundleCompactPreviewHtml === 'function',
    },
    {
      file: 'pb-offer-intro.js',
      ready: () => typeof window.productBuilder?.armOfferIntroPrehide === 'function',
    },
  ];

  for (const { file, ready } of scripts) {
    if (ready()) continue;
    const url = resolveThemeAssetUrl(file);
    if (!url) {
      console.warn('[pb-builder-launch] Offer support module URL missing:', file);
      continue;
    }
    try {
      await loadExternalScript(url, { force: true });
    } catch (err) {
      console.warn('[pb-builder-launch] Offer support module load failed:', file, err?.message || err);
    }
  }
  await ensureOfferBundleSyncPatched();
  installBuilderCapabilities();
}

/** @deprecated Use installBuilderCapabilities */
export function patchBuilderAugments(pb) {
  installBuilderCapabilities(pb || window.productBuilder);
}

function patchOfferLineGuards() {
  installBuilderCapabilities();
}
export async function ensureStepPluginModulesReady() {
  const imports = [];
  if (!window.PBBuilderLifecycle?.createBuilderLifecycle) {
    imports.push(import('@theme/pb-builder-lifecycle'));
  }
  if (!window.PBNavigation?.createNavigationKernel) {
    imports.push(import('@theme/pb-navigation'));
  }
  if (!window.PBOffers?.createOffersPlugin) {
    imports.push(import('@theme/pb-offers'));
  }
  if (!window.PBAddons?.createAddonsPlugin) {
    imports.push(import('@theme/pb-addons'));
  }
  if (imports.length) {
    await Promise.all(
      imports.map((p) =>
        p.catch((err) => {
          console.warn('[pb-builder-launch] Step plugin module load failed', err?.message || err);
        })
      )
    );
  }
  patchBuilderAugments();
  if (typeof window.productBuilder?._wireLifecycleStepPlugins === 'function') {
    window.productBuilder._wireLifecycleStepPlugins();
  }
}

/** Re-apply tag-module patches after lazy portal mount creates productBuilder. */
/** Wait for bundle encode belt before row save — no silent fallback that drops design props. */
export function wrapFinishSubFlowEncodeBelt(pb) {
  const target = pb || window.productBuilder;
  if (!target || target.__pbFinishSubFlowEncodeBeltWrapped) return;
  const orig = target.finishSubFlow;
  if (typeof orig !== 'function') return;
  target.finishSubFlow = function wrappedFinishSubFlowEncodeBelt() {
    const s = window.builderState;
    const needsBelt = !!(s && s.sub && s.sub.isQueueItem);
    const runOrig = () => orig.apply(this, arguments);
    if (!needsBelt) {
      runOrig();
      return;
    }
    if (window.PBBundleRowEncode?.encodeManualBundleRowSave) {
      runOrig();
      return;
    }
    const btn = document.getElementById('nextBtn');
    const prevText = btn ? btn.innerText : '';
    if (btn) {
      btn.disabled = true;
      btn.innerText = 'Saving…';
    }
    ensureEncodeBeltModulesReady()
      .then(() => {
        if (btn) {
          btn.disabled = false;
          btn.innerText = prevText;
        }
        if (!window.PBBundleRowEncode?.encodeManualBundleRowSave) {
          alert('Personalization engine still loading. Please wait a moment and try again.');
          return;
        }
        runOrig();
      })
      .catch(() => {
        if (btn) {
          btn.disabled = false;
          btn.innerText = prevText;
        }
        alert('Personalization engine still loading. Please wait a moment and try again.');
      });
  };
  target.__pbFinishSubFlowEncodeBeltWrapped = true;
}

/** Collection portal + bundle offer finalize — encode belt gate before cart write. */
const FINALIZE_BELT_TIMEOUT_MS = 20000;
const FINALIZE_BELT_LOADING_MESSAGE =
  'Personalization engine still loading. Please wait a moment and try again.';

function withFinalizeBeltTimeout(promise, ms = FINALIZE_BELT_TIMEOUT_MS) {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error('FINALIZE_BELT_TIMEOUT')), ms);
    }),
  ]);
}

function finalizeEncodeBeltAlreadyReady(pb, { collectionCard, needsBundleBelt }) {
  if (collectionCard) {
    return isEncodeBeltReady(pb) && areOfferRenderDepsReady(pb);
  }
  if (needsBundleBelt) {
    return !!window.PBBundleRowEncode?.encodeBundleCartRowProperties;
  }
  return true;
}

async function awaitFinalizeEncodeBeltReady(pb, { collectionCard }) {
  await withFinalizeBeltTimeout(ensureEncodeBeltModulesReady());
  if (collectionCard) {
    await withFinalizeBeltTimeout(ensureOfferRenderDepsReady());
    assertEncodeBeltReadyLocal(pb, 'wrapFinalizeEncodeBelt');
    if (!areOfferRenderDepsReady(pb)) {
      throw new Error('OFFER_RENDER_DEPS_NOT_READY');
    }
  }
}

/** Gate offer finalize on encode belt for collection_card portal and bundle add-to-cart. */
export function wrapFinalizeEncodeBelt(pb) {
  const target = pb || window.productBuilder;
  if (!target || target.__pbFinalizeEncodeBeltWrapped) return;
  const orig = target.finalize;
  if (typeof orig !== 'function') return;
  target.finalize = function wrappedFinalizeEncodeBelt() {
    const s = window.builderState;
    const collectionCard = isCollectionCardEntryManifest(readEntryManifest(s));
    const needsBundleBelt = !!(s && s.bundle && s.bundle.selectedId);
    const needsBelt = collectionCard || needsBundleBelt;
    const runOrig = () => orig.apply(this, arguments);
    if (!needsBelt) {
      return runOrig();
    }
    if (finalizeEncodeBeltAlreadyReady(target, { collectionCard, needsBundleBelt })) {
      return runOrig();
    }
    const btn = document.getElementById('nextBtn');
    const prevText = btn ? btn.innerText : '';
    const prevDisabled = btn ? btn.disabled : false;
    if (btn) {
      btn.disabled = true;
      btn.innerText = 'Adding…';
    }
    return awaitFinalizeEncodeBeltReady(target, { collectionCard })
      .then(() => {
        if (needsBundleBelt && !window.PBBundleRowEncode?.encodeBundleCartRowProperties) {
          throw new Error('BUNDLE_ENCODE_BELT_MISSING');
        }
        return runOrig();
      })
      .catch(() => {
        alert(FINALIZE_BELT_LOADING_MESSAGE);
        return undefined;
      })
      .finally(() => {
        if (btn) {
          btn.disabled = prevDisabled;
          btn.innerText = prevText;
        }
      });
  };
  target.__pbFinalizeEncodeBeltWrapped = true;
}

export function wrapFinishSubFlowBundleSync(pb) {
  const target = pb || window.productBuilder;
  if (!target || target.__pbFinishSubFlowSyncWrapped) return;
  const orig = target.finishSubFlow;
  if (typeof orig !== 'function') return;
  target.finishSubFlow = function wrappedFinishSubFlow() {
    const s = window.builderState;
    const wasQueue = !!(s && s.sub && s.sub.isQueueItem);
    const bid = s && s.bundle ? String(s.bundle.selectedId || '') : '';
    orig.apply(this, arguments);
    if (typeof window !== 'undefined' && window.PBAiCatalogScope?.purgeOrphanAiImageByLabel) {
      window.PBAiCatalogScope.purgeOrphanAiImageByLabel(window.builderState);
    }
    if (typeof window.productBuilder?.checkStepValidity === 'function') {
      window.productBuilder.checkStepValidity();
    }
    if (!wasQueue || !bid) return;
    patchBuilderAugments(target);
    let synced = false;
    if (typeof target.inlineSyncBundleRowsAfterSave === 'function') {
      const bundle = window.BUILDER_DATA?.bundles?.find((b) => String(b && b.id) === bid);
      const idx = parseInt(s.sub.currentId, 10);
      const slot = s.bundle.configurations[bid] && s.bundle.configurations[bid][idx];
      if (bundle && slot) {
        synced = !!target.inlineSyncBundleRowsAfterSave(
          bid,
          bundle,
          idx,
          slot.properties || {},
          slot.id
        );
      }
    } else if (typeof target.maybeAutoApplyBundleSync === 'function') {
      synced = !!target.maybeAutoApplyBundleSync(bid);
    }
    if (synced) {
      target.invalidateOffersListContentHash?.();
      target.renderAddons?.();
      target.renderOffers?.({ intro: 'skip' });
      target.checkOfferValidity?.();
    }
  };
  target.__pbFinishSubFlowSyncWrapped = true;
}

function removeHoistedBuilderRoot() {
  const root = document.getElementById('product-builder-root');
  if (!root || root.closest('[id^="shopify-section-"]')) return;
  window.pbAudioEngine?.disposeFieldScope(root);
  root.remove();
}

function hoistBuilderFromPortal() {
  const portal = document.getElementById('pb-cart-edit-portal');
  const root = portal?.querySelector('#product-builder-root');
  if (!root) return;
  document.body.appendChild(root);
  portal.hidden = true;
  portal.setAttribute('aria-hidden', 'true');
}

/** Return hoisted builder root to cart-edit portal after close/failure. */
export function restoreBuilderRootToPortal() {
  if (typeof document === 'undefined') return;
  const portal = document.getElementById('pb-cart-edit-portal');
  const root = document.getElementById('product-builder-root');
  if (!portal || !root || portal.contains(root)) return;
  if (root.closest('[id^="shopify-section-"]')) return;
  portal.appendChild(root);
  portal.hidden = true;
  portal.setAttribute('aria-hidden', 'true');
}

export function applyFreshSessionState() {
  resetBuilderStateForStartFresh(window.builderState);
  if (window.productBuilder && typeof window.productBuilder.reconcileStepDom === 'function') {
    window.productBuilder.reconcileStepDom();
  }
}

function wireLoadingShellClose() {
  if (loadingShellCloseWired) return;
  const shell = document.getElementById('pb-card-loading-shell');
  const closeBtn = shell?.querySelector('[data-pb-loading-shell-close]');
  if (!closeBtn) return;
  loadingShellCloseWired = true;
  closeBtn.addEventListener('click', () => {
    loadingShellAborted = true;
    closeBuilderLoadingShell();
  });
}

export function wasLoadingShellAborted() {
  return loadingShellAborted;
}

export function openBuilderLoadingShell() {
  loadingShellAborted = false;
  wireLoadingShellClose();
  const shell = document.getElementById('pb-card-loading-shell');
  if (!shell) return;
  shell.hidden = false;
  shell.removeAttribute('aria-hidden');
  document.body.classList.add('builder-closing');
}

export function closeBuilderLoadingShell() {
  const shell = document.getElementById('pb-card-loading-shell');
  if (shell) {
    shell.hidden = true;
    shell.setAttribute('aria-hidden', 'true');
  }
  const modal = document.getElementById('builder-modal');
  const modalOpen = modal && modal.style.display === 'block';
  if (!modalOpen && !document.body.classList.contains('builder-open')) {
    document.body.classList.remove('builder-closing');
  }
}

async function fetchBuilderSectionHtml(handle) {
  const cacheKey = getSectionCacheKey(handle);
  const pending = prefetchInFlight.get(cacheKey);
  if (pending) await pending;
  if (sectionHtmlCache.has(cacheKey)) return sectionHtmlCache.get(cacheKey);

  const url = builderSectionUrl(handle);
  let html = '';
  for (let attempt = 1; attempt <= 4; attempt += 1) {
    const res = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
    if (res.ok) {
      html = await res.text();
      break;
    }
    if (attempt < 4 && (res.status === 429 || res.status === 503)) {
      await new Promise((r) => setTimeout(r, 1500 * attempt));
      continue;
    }
    throw new Error('Could not load product builder');
  }

  if (sectionHtmlMatchesEpoch(html, getBuilderCacheEpoch(window))) {
    sectionHtmlCache.set(cacheKey, html);
    touchSectionCache(cacheKey);
  }
  return html;
}

async function mountBuilderSection(handle) {
  const html = await fetchBuilderSectionHtml(handle);
  const portal = document.getElementById('pb-cart-edit-portal');
  if (!portal) throw new Error('Cart edit portal missing');
  window.pbAudioEngine?.disposeFieldScope(portal);
  portal.innerHTML = html;
  portal.hidden = false;
  portal.removeAttribute('aria-hidden');
  await runInlineScripts(portal);
  await ensureBuilderCapabilitiesInstalled(window.productBuilder);
  ensureFieldRuntimeOnBuilder();
  await ensureTagModulesReady();
  await ensureFoundationModulesReady();
  await ensureEncodeBeltModulesReady();
  await ensureOfferRenderDepsReady();
  installBuilderCapabilities(window.productBuilder);
  ensureFieldRuntimeOnBuilder();
  hoistBuilderFromPortal();
}

/**
 * @param {string} handle
 * @param {string|number} productId
 * @param {{ freshSession?: boolean, skipDraftPrompt?: boolean, pendingDraft?: object|null }} [options]
 */
export async function ensureBuilderForProduct(handle, productId, options) {
  const explicitFresh = options?.freshSession === true;
  const skipDraftPrompt = options?.skipDraftPrompt === true;
  const alreadyOnPage = builderAlreadyOnPage(productId);
  const draftExists =
    !skipDraftPrompt &&
    !alreadyOnPage &&
    !explicitFresh &&
    !!loadDraftForProduct(productId);
  const freshSession = shouldResetBuilderSessionOnOpen(productId, explicitFresh, {
    builderAlreadyOnPage: alreadyOnPage,
    draftExists: skipDraftPrompt ? !!options?.pendingDraft : draftExists,
  });

  let pendingDraft = skipDraftPrompt ? options?.pendingDraft ?? null : null;

  if (alreadyOnPage) {
    await ensureBuilderCapabilitiesInstalled(window.productBuilder);
    await ensureTagModulesReady();
    await ensureFoundationModulesReady();
    await ensureEncodeBeltModulesReady();
    await ensureOfferRenderDepsReady();
    if (freshSession) {
      applyFreshSessionState();
      if (window.productBuilder) {
        window.productBuilder._pbColdInitDone = false;
        window.productBuilder.ready = false;
        window.productBuilder.init();
        await waitForBuilderReady();
      }
    } else if (window.productBuilder && !window.productBuilder._pbColdInitDone) {
      window.productBuilder.init();
      await waitForBuilderReady();
    }
    wireDraftAutoSave(productId);
    installCloseResumeHook();
    wireLazyBuilderChrome();
    return;
  }

  removeHoistedBuilderRoot();

  const portal = document.getElementById('pb-cart-edit-portal');
  if (portal) {
    window.pbAudioEngine?.disposeFieldScope(portal);
    portal.innerHTML = '';
  }

  if (window.productBuilder) {
    window.productBuilder._pbColdInitDone = false;
    window.productBuilder.ready = false;
  }

  if (!pbAssetsSessionLoaded) {
    removeStaleAssetScripts();
  }

  await mountBuilderSection(handle);
  ensureFieldRuntimeOnBuilder();

  if (!skipDraftPrompt && draftExists) {
    const resume = await resolveDraftResume(productId, { applyFreshSessionState });
    if (resume.choice === 'continue' && resume.draft) {
      pendingDraft = resume.draft;
    }
  } else if (explicitFresh && !skipDraftPrompt) {
    applyFreshSessionState();
  }

  await ensureTagModulesReady();
  await ensureBuilderInitDepsReady();

  if (window.productBuilder && typeof window.productBuilder.init === 'function') {
    window.productBuilder.init();
  }

  try {
    await waitForBuilderReady(45000);
  } catch (readyErr) {
    if (window.productBuilder && window.BUILDER_DATA?.mainProduct && !window.productBuilder.ready) {
      console.warn('[pb-builder-launch] Forcing builder ready after portal mount:', readyErr?.message || readyErr);
      window.productBuilder.ready = true;
      document.dispatchEvent(new CustomEvent('builder:ready'));
    } else {
      throw readyErr;
    }
  }

  if (pendingDraft) {
    window.__pbPendingDraftHydration = applyDraftStateAfterInit(pendingDraft);
  }

  await ensureFoundationModulesReady();
  await ensureEncodeBeltModulesReady();
  await ensureOfferRenderDepsReady();
  wireDraftAutoSave(productId);
  installCloseResumeHook();
  wireLazyBuilderChrome();
}

/** @deprecated Use ensureBuilderForProduct(..., { freshSession: true }) */
export function resetBuilderForFreshSession() {
  applyFreshSessionState();
  const pb = window.productBuilder;
  if (pb) {
    pb._pbColdInitDone = false;
    pb.ready = false;
    if (typeof pb.init === 'function') pb.init();
  }
}

export function wireLazyBuilderChrome() {
  const close = document.getElementById('closeBuilderBtn');
  if (!close || close.dataset.pbCartEditWired) return;
  close.dataset.pbCartEditWired = '1';
  if (!close.getAttribute('onclick')) {
    close.setAttribute('onclick', 'window.productBuilder && window.productBuilder.close()');
  }
}

export async function suspendCartDrawerForBuilder() {
  const component = document.querySelector('cart-drawer-component');
  const dialog = component?.refs?.dialog || component?.querySelector?.('dialog');
  if (!component || !dialog?.open) {
    suspendedCartDrawer = null;
    return;
  }
  suspendedCartDrawer = component;
  try {
    await component.closeDialog();
  } catch (e) {
    suspendedCartDrawer = null;
  }
}

function resumeCartDrawerAfterBuilder(drawer) {
  if (!drawer || typeof drawer.open !== 'function') return;
  window.setTimeout(() => {
    try {
      drawer.open();
    } catch (e) {}
  }, BUILDER_CLOSE_RESUME_MS);
}

function wireModalClosedCartResume() {
  if (wireModalClosedCartResume._wired) return;
  wireModalClosedCartResume._wired = true;
  document.addEventListener('pb:builder-modal-closed', () => {
    const drawer = pendingCartDrawerResume;
    pendingCartDrawerResume = null;
    if (drawer) resumeCartDrawerAfterBuilder(drawer);
  });
}

export function installCloseResumeHook() {
  wireModalClosedCartResume();
  const pb = window.productBuilder;
  if (!pb || closeResumeHookTarget === pb) return;
  closeResumeHookTarget = pb;
  const originalClose = pb.close.bind(pb);
  pb.close = function pbAwareClose(options) {
    if (window.builderState?.cartEdit?.pendingSave || window.builderState?.cartEdit?.saving) return;
    const drawer = suspendedCartDrawer;
    suspendedCartDrawer = null;
    if (drawer && resolveBuilderCloseMode(window.builderState, options) === 'full') {
      pendingCartDrawerResume = drawer;
    }
    originalClose(options);
  };
}

function assertCartResponse(parsed, fallbackMessage) {
  if (!parsed || typeof parsed !== 'object') {
    throw new Error(fallbackMessage);
  }
  if (parsed.errors) {
    throw new Error(typeof parsed.errors === 'string' ? parsed.errors : fallbackMessage);
  }
  if (parsed.status && parsed.description) {
    throw new Error(parsed.description);
  }
}

async function postCartAdd(payload) {
  const surprise=senderSurpriseCartOptions();
  const prepared=await prepareSenderSurpriseCart(payload.items,surprise);
  const body = JSON.stringify({...payload,items:prepared});
  // PBPIG-1 — the builder is the authority on personalization, so its own adds skip the cart-add
  // guard. Without this an add-on line the builder legitimately adds with no properties would be
  // bounced straight back into the builder.
  const res = await fetch(
    getThemeRoutes().cart_add_url,
    fetchConfig('json', { body, headers: { [GUARD_BYPASS_HEADER]: GUARD_BYPASS_VALUE } })
  );
  const parsed = await res.json();
  assertCartResponse(parsed, 'Could not add to cart');
  if(surprise.enabled)completeSenderSurpriseCart(surprise.variantId,undefined,surprise.sourceDraft);
  return parsed;
}

/**
 * Add line items, refresh cart sections, open cart drawer, optionally close builder.
 * @param {Array<{id: number, quantity: number, properties?: object}>} items
 * @param {HTMLButtonElement | null} btn
 * @param {string} originalText
 * @param {{ closeBuilder?: boolean }} [options]
 */
export async function addItemsToCartWithDrawer(items, btn, originalText, options) {
  if (btn && window.productBuilder?.landingSaveOnly) {
    const capture = window.__pbLandingProductCapture;
    if (!capture?.matches?.(window.productBuilder, window.builderState, btn)) {
      btn.disabled = false;
      btn.innerText = 'Save gift';
      throw new Error('Reopen your gift to save it. No product was added to your cart.');
    }
    return capture.save(items, btn, originalText);
  }

  const closeBuilder = options?.closeBuilder !== false;
  const sectionsPayload = buildCartSectionsPayload();
  const lineSessionStates = (items || []).map((item) => item?.__pbSessionState ?? null);
  const shopifyItems = (items || []).map((item) => {
    if (!item || typeof item !== 'object') return item;
    const { __pbSessionState, ...rest } = item;
    return rest;
  });

  if (btn) {
    btn.innerText = 'Adding...';
    btn.disabled = true;
  }

  try {
    const parsed = await postCartAdd({
      items: shopifyItems,
      ...sectionsPayload,
    });
    refreshCartSections(parsed, { source: 'pb-builder-launch', itemCount: parsed.item_count });
    const productId = window.BUILDER_DATA?.mainProduct?.id;
    const builderState = window.builderState;
    if (builderState && !builderState.cartEdit) {
      persistCartSessionsAfterAdd(parsed, shopifyItems, builderState, { lineSessionStates });
    }
    if (productId != null) {
      clearDraftAfterCartSuccess(productId, {
        cartEdit: !!builderState?.cartEdit,
      });
    }
    if (closeBuilder && window.productBuilder) {
      window.productBuilder.close();
    }
  } catch (err) {
    alert('Error adding to cart: ' + (err?.message || 'Unknown error'));
    if (btn) {
      btn.innerText = originalText || 'Continue';
      btn.disabled = false;
    }
    throw err;
  }
}

if (typeof window !== 'undefined') {
  window.__pbAddItemsToCart = addItemsToCartWithDrawer;
  window.__pbClearBuilderSectionCache = () => clearSectionHtmlCache(sectionCacheState);
  window.__pbResolveBuilderCloseMode = resolveBuilderCloseMode;
  window.__pbSuspendCartDrawerForBuilder = suspendCartDrawerForBuilder;
  // Readiness window hooks owned by pb-entry-readiness (imported above).
}
